/**
 * NeedsAttention — lo primero de la pantalla y lo único que realmente urge.
 *
 * Por defecto se ven cuatro; el resto está a un toque. Cada aviso cabe en dos
 * líneas: gravedad, unidad o huésped, el problema, el contexto justo y una
 * sola acción.
 *
 * MAQUETA. Cuando exista backend estos elementos NO se calculan aquí: los
 * emite el Action Center (`GET /api/actions`, `backend/actions_engine.py`)
 * como una fuente más, con el mismo `action_id`, los mismos CTA y el mismo
 * snooze/dismiss (PRODUCT_V2_SPEC §5.2 y §16.1). Hoy es una vista; si algún
 * día esta pantalla empieza a guardar sus propios pendientes, se ha roto el
 * modelo.
 */
import React, { useState } from "react";
import { URGENCY_TAG } from "@/data/todayV2Demo";
import {
  TodayBlock, TodayCard, StatusPill, QuickAction, EmptyState, ShowMore,
} from "@/pages/admin/pms/v2/V2Primitives";

const VISIBLE_BY_DEFAULT = 4;

export default function NeedsAttention({ items, onGoToSection }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, VISIBLE_BY_DEFAULT);
  const hidden = items.length - visible.length;

  return (
    <TodayBlock
      id="today-attention"
      title="Necesita atención"
      count={items.length}
      testId="today-attention"
    >
      {items.length === 0 ? (
        <EmptyState testId="today-attention-empty">Nada pendiente ahora mismo.</EmptyState>
      ) : (
        <>
          <div className="space-y-1.5" data-testid="today-attention-list">
            {visible.map((item) => (
              <AttentionCard key={item.action_id} item={item} onGoToSection={onGoToSection} />
            ))}
          </div>

          {(hidden > 0 || expanded) && (
            <ShowMore
              expanded={expanded}
              onToggle={() => setExpanded((v) => !v)}
              moreLabel={`Ver las ${items.length}`}
              testId="today-attention-more"
            />
          )}
        </>
      )}
    </TodayBlock>
  );
}

function AttentionCard({ item, onGoToSection }) {
  const tag = URGENCY_TAG[item.urgency] || URGENCY_TAG.INFO;
  const critical = item.urgency === "CRITICAL";

  return (
    <TodayCard
      tone={critical ? "critical" : "warn"}
      padding="px-3 py-2.5"
      testId={`attention-${item.action_id}`}
      className="flex items-start gap-3"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill tone={tag.tone}>{tag.label}</StatusPill>
          <span className="text-sm text-stone-900 dark:text-stone-100 min-w-0">{item.title}</span>
        </div>
        <div className="text-[11px] text-stone-500 dark:text-stone-400 mt-0.5">
          {[item.reason, item.meta].filter(Boolean).join(" · ")}
        </div>
      </div>

      <div className="shrink-0">
        <QuickAction
          label={item.cta.label}
          to={item.cta.to}
          onClick={item.cta.section ? () => onGoToSection(item.cta.section) : undefined}
          primary={critical}
          testId={`attention-cta-${item.action_id}`}
        />
      </div>
    </TodayCard>
  );
}
